import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface DocumentPictureInPicture {
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
}

type WindowWithDocumentPictureInPicture = Window & {
  documentPictureInPicture?: DocumentPictureInPicture;
};

export interface UseDocumentPictureInPictureOptions {
  width?: number;
  height?: number;
  /**
   * Browser close button on the PiP window. Chrome focuses the opener before closing for
   * back-to-tab, and does not for the close button, so `pagehide` while the opener is
   * unfocused is the close button.
   */
  onUserDismiss?: () => void;
  /** Override for tests. */
  ownerWindow?: Window;
}

function getDocumentPictureInPicture(ownerWindow: Window): DocumentPictureInPicture | undefined {
  return (ownerWindow as WindowWithDocumentPictureInPicture).documentPictureInPicture;
}

function copyDocumentStyles(source: Document, targetWindow: Window): void {
  const target = targetWindow.document;
  target.documentElement.className = source.documentElement.className;
  target.body.className = source.body.className;
  target.documentElement.style.height = '100%';
  target.body.style.margin = '0';
  target.body.style.height = '100%';
  target.body.style.background = 'transparent';

  for (const node of Array.from(source.head.querySelectorAll('link[rel="stylesheet"], style'))) {
    target.head.appendChild(node.cloneNode(true));
  }
}

/**
 * Owns a Document Picture-in-Picture window: support check, open/close, and `pagehide` cleanup.
 *
 * The PiP document is blank until styles are copied (done in `open`) and you mount content into
 * `pipWindow`. Call `open` from a user gesture.
 */
export function useDocumentPictureInPicture({
  width,
  height,
  onUserDismiss,
  ownerWindow = window,
}: UseDocumentPictureInPictureOptions = {}) {
  const isSupported = useMemo(
    () => typeof getDocumentPictureInPicture(ownerWindow)?.requestWindow === 'function',
    [ownerWindow],
  );

  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const pipWindowRef = useRef<Window | null>(null);
  const ignoreNextPageHideRef = useRef(false);

  const clear = useCallback(() => {
    pipWindowRef.current = null;
    setPipWindow(null);
  }, []);

  const close = useCallback(() => {
    const current = pipWindowRef.current;
    if (current && !current.closed) {
      ignoreNextPageHideRef.current = true;
      current.close();
    }
    clear();
  }, [clear]);

  const open = useCallback(async (): Promise<Window | null> => {
    close();

    const api = getDocumentPictureInPicture(ownerWindow);
    if (!api) {
      return null;
    }

    try {
      const nextWindow = await api.requestWindow(width != null || height != null ? { width, height } : undefined);
      copyDocumentStyles(ownerWindow.document, nextWindow);
      pipWindowRef.current = nextWindow;
      setPipWindow(nextWindow);
      return nextWindow;
    } catch {
      return null;
    }
  }, [close, height, ownerWindow, width]);

  useEffect(() => {
    return () => {
      const current = pipWindowRef.current;
      if (current && !current.closed) {
        ignoreNextPageHideRef.current = true;
        current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (!pipWindow) {
      return;
    }

    const onPageHide = () => {
      if (pipWindowRef.current !== pipWindow) {
        return;
      }
      const dismissedByBrowserClose = !ignoreNextPageHideRef.current && !ownerWindow.document.hasFocus();
      ignoreNextPageHideRef.current = false;
      clear();
      if (dismissedByBrowserClose) {
        onUserDismiss?.();
      }
    };
    pipWindow.addEventListener('pagehide', onPageHide);
    return () => pipWindow.removeEventListener('pagehide', onPageHide);
  }, [clear, onUserDismiss, ownerWindow, pipWindow]);

  return { isSupported, pipWindow, open, close };
}
